import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useInView } from "@/hooks/useInView";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export const plans = [
  {
    name: "Athletic Meals",
    dailyPrice: "1700",
    monthlyPrice: "42,400",
    description: "Perfect for individuals starting their athletic journey",
    features: [
      "Full-day meal plan (Breakfast, Lunch, Dinner)",
      "Basic meal customization",
      "Standard delivery",
      "Nutrition tracking",
      "Custom recipe requests",
      "24/7 email and phone support",
    ],
    popular: false,
  },
  {
    name: "Weight Loss Meals",
    dailyPrice: "1600",
    monthlyPrice: "40,800",
    description: "Perfect for individuals starting their weight loss journey",
    features: [
      "Full-day meal plan (Breakfast, Lunch, Dinner)",
      "Basic meal customization",
      "Standard delivery",
      "Nutrition tracking",
      "Custom recipe requests",
      "24/7 email and phone support",
    ],
    popular: true,
  },
  {
    name: "Diabetes & hypertension therapeutic Meals",
    dailyPrice: "2200",
    monthlyPrice: "50,600",
    description: "Perfect for individuals with specific dietary needs",
    features: [
      "Full-day meal plan (Breakfast, Lunch, Dinner)",
      "Basic meal customization",
      "Standard delivery",
      "Nutrition tracking",
      "Custom recipe requests",
      "24/7 email and phone support",
      "Medical nutrition therapy",
    ],
    popular: false,
  },
  {
    name: "Meal Planing & Dietician Counseling",
    dailyPrice: "15,500",
    period: "one time",
    description: "Perfect for individuals to improve their health",
    features: [
      "Meal plan",
      "dietician counseling for a month",
      "24/7 email and phone support",
    ],
    popular: false,
  },
  {
    name: "Meal Planing",
    price: "8000",
    period: "one time",
    description: "Perfect for individuals to improve their health",
    features: ["Meal plan", "24/7 email and phone support"],
    popular: false,
  },
  {
    name: "healthy Buffet For Events",
    price: "910",
    period: "per plate",
    description: "Perfect for individuals to improve their health",
    features: [
      "Basic meal customization",
      "Standard delivery",
      "Custom recipe requests",
      "24/7 email and phone support",
    ],
    popular: false,
  },
];

const PricingSection = () => {
  const [isMonthly, setIsMonthly] = useState(false);
  const { ref: headerRef, isInView: headerInView } = useInView({
    threshold: 0.2,
  });
  const { ref: cardsRef, isInView: cardsInView } = useInView({
    threshold: 0.1,
  });

  const scrollToRegistration = () => {
    document
      .getElementById("registration")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="pricing" className="section-padding bg-background">
      <div className="container-narrow">
        <div
          ref={headerRef}
          className={`text-center space-y-6 mb-16 transition-all duration-1000 ${
            headerInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl sm:text-5xl font-bold font-heading text-foreground">
            Choose Your <span className="text-primary">Perfect Plan</span>
          </h2>
          <p className="text-xl text-muted-foreground font-body leading-relaxed max-w-3xl mx-auto">
            Flexible pricing options designed to fit your lifestyle and budget.
            All plans include fresh, locally-sourced ingredients and expert
            nutrition guidance.
          </p>

          <div className="flex items-center justify-center space-x-4 mt-8">
            <Label
              htmlFor="pricing-toggle"
              className={`font-body ${
                !isMonthly ? "text-primary font-bold" : "text-muted-foreground"
              }`}
            >
              Daily
            </Label>
            <Switch
              id="pricing-toggle"
              checked={isMonthly}
              onCheckedChange={setIsMonthly}
            />
            <Label
              htmlFor="pricing-toggle"
              className={`font-body ${
                isMonthly ? "text-primary font-bold" : "text-muted-foreground"
              }`}
            >
              Monthly
            </Label>
          </div>

          <div className="mt-8 text-lg text-muted-foreground font-body leading-relaxed max-w-3xl mx-auto bg-gray-100 dark:bg-gray-800 p-6 rounded-xl">
            <p className="font-semibold text-foreground mb-4">
              If you subscribe to our monthly plan you get these extras:
            </p>
            <ul className="space-y-2 text-left text-base">
              <li className="flex items-start">
                <span className="mr-3">🥤</span>
                <span>Functional juices (detox • athletic • therapeutic)</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3">🍰</span>
                <span>Healthy treats for weight-loss clients</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3">👨‍⚕️</span>
                <span>FREE dietitian guidance</span>
              </li>
            </ul>
          </div>
        </div>

        <div ref={cardsRef} className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`w-full relative bg-white rounded-3xl shadow-soft card-hover transition-all duration-700 ${
                plan.popular
                  ? "border-2 border-primary scale-105"
                  : "border border-border"
              } ${
                cardsInView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-primary text-white px-6 py-2 rounded-full text-sm font-semibold font-body">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="p-8 space-y-6">
                <div className="text-center space-y-2">
                  <h3 className="text-2xl font-bold font-heading text-foreground">
                    {plan.name}
                  </h3>
                  <p className="text-muted-foreground font-body text-sm">
                    {plan.description}
                  </p>
                </div>

                <div className="text-center space-y-1">
                  <div className="flex items-center justify-center space-x-1">
                    <span className="text-sm text-muted-foreground">ETB</span>
                    <span className="text-4xl font-bold font-heading text-foreground">
                      {plan.dailyPrice
                        ? isMonthly
                          ? plan.monthlyPrice
                          : plan.dailyPrice
                        : plan.price}
                    </span>
                  </div>
                  <p className="text-muted-foreground font-body text-sm">
                    {plan.dailyPrice
                      ? isMonthly
                        ? "per month"
                        : "per day"
                      : plan.period}
                  </p>
                </div>

                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start space-x-3"
                    >
                      <div className="w-5 h-5 bg-primary-light rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary text-xs">✓</span>
                      </div>
                      <span className="text-muted-foreground font-body text-sm">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full btn-hover-lift font-medium ${
                    plan.popular
                      ? "bg-primary hover:bg-primary-hover text-white"
                      : "bg-white border-2 border-primary text-primary hover:bg-primary-light"
                  }`}
                  size="lg"
                  onClick={scrollToRegistration}
                >
                  <span className="block truncate">
                    {" "}
                    {/* <-- Add these classes */}
                    Choose
                  </span>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground font-body text-sm">
            All plans include free delivery within Addis Ababa.
            {/* <span className="text-primary font-medium">
              {" "}
              Special discounts available for students and seniors.
            </span> */}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
